import React, { useMemo, useState } from 'react'
import { graphql, useFragment } from 'react-relay'

import { YStack, Separator, YStackProps } from 'tamagui'

import { Card } from '@newComponents/Card'
import { H2, Text } from '@newComponents/Text'
import { Button } from '@newComponents/Button/Button'

import { TierSelectionItem } from './TierSelectionItem'

import { TierSelectionCard_event$key } from './__generated__/TierSelectionCard_event.graphql'

export const TierSelectionCard = ({
  event,
  onConfirm,
  confirmLabel = 'Proceed to Checkout',
  initialSelections,
  ...cardProps
}: {
  event: TierSelectionCard_event$key
  onConfirm: (tierSelections: Record<string, number>) => void
  confirmLabel?: string
  initialSelections?: Record<string, number>
} & Omit<YStackProps, 'children'>) => {
  const data = useFragment(
    graphql`
      fragment TierSelectionCard_event on Event {
        name
        eventTicketingTiers(orderBy: [SORT_ORDER_ASC, CREATED_AT_ASC]) {
          edges {
            node {
              id
              status
              ...TierSelectionItem_tier
            }
          }
        }
      }
    `,
    event
  )

  const [tierSelections, setTierSelections] = useState<
    Record<string, number>
  >(initialSelections ?? {})

  const visibleTierEdges = useMemo(
    () =>
      data.eventTicketingTiers.edges.filter(
        (edge) => edge.node && edge.node.status !== 'hidden'
      ),
    [data.eventTicketingTiers.edges]
  )

  const totalSelectedTickets = useMemo(
    () => Object.values(tierSelections).reduce((sum, qty) => sum + qty, 0),
    [tierSelections]
  )

  if (visibleTierEdges.length === 0) {
    return null
  }

  return (
    <Card gap='$8' {...cardProps}>
      <YStack gap='$1'>
        <H2>Select your tickets</H2>
        <Text size='$5'>Choose tickets for {data.name}</Text>
      </YStack>

      {visibleTierEdges.map((edge, index) => (
        <React.Fragment key={edge.node!.id}>
          {index !== 0 && <Separator borderColor='$borderColor' />}

          <TierSelectionItem
            tier={edge.node!}
            quantity={tierSelections[edge.node!.id] || 0}
            onQuantityChange={(qty) =>
              setTierSelections((prev) => ({
                ...prev,
                [edge.node!.id]: qty,
              }))
            }
          />
        </React.Fragment>
      ))}

      <YStack alignItems='center'>
        <Button
          onPress={() => onConfirm(tierSelections)}
          disabled={totalSelectedTickets === 0}
        >
          <Button.Text>
            {totalSelectedTickets > 0
              ? `${confirmLabel} (${totalSelectedTickets} ${totalSelectedTickets === 1 ? 'ticket' : 'tickets'})`
              : 'Select tickets to continue'}
          </Button.Text>
        </Button>
      </YStack>
    </Card>
  )
}
